import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import * as d3 from 'd3';
import type { BreadthPoint } from '../../services/zerodayBreadthService';
import { histogramTone } from './histogramTone';
import { SESSION_SLOTS, sessionDays, slotAt, toSessionRuns, type SessionRun, type SlotPoint } from './sessionAxis';

const COLORS = {
  gridLines: '#1e2433',
  text: '#9ca3af',
  watermark: 'rgba(244, 242, 237, 0.06)',
  crosshair: 'rgba(244, 242, 237, 0.35)',
  up: '#1C76A2',
  down: '#D8483B',
};

export interface BreadthPanelProps {
  label: string;
  points: readonly BreadthPoint[];
  /** Line colour. Omit for a zero-centred series coloured by sign. */
  color?: string;
  /**
   * Second line on its own y-axis (left). VIX and VVIX do not share a scale.
   * Placed on the same session slots as `points`.
   */
  overlayPoints?: readonly BreadthPoint[];
  overlayColor?: string;
  /** Replaces the bold label. The vol pane uses this for its VIX / VVIX toggles. */
  controls?: ReactElement;
  /** Draw a dashed zero line and colour the line by sign (ADD/ADDQ). */
  zeroCentered?: boolean;
  /** Bars from zero, coloured by the Watchdog rule (MAG7). Implies a zero baseline. */
  histogram?: boolean;
  decimals: number;
}

interface DrawnScales {
  x: d3.ScaleLinear<number, number>;
  y: d3.ScaleLinear<number, number>;
  yOverlay: d3.ScaleLinear<number, number> | null;
  innerH: number;
  color: string;
  overlayColor: string | null;
}

function pointAt(runs: readonly SessionRun[], time: number): SlotPoint | undefined {
  for (const run of runs) {
    for (const point of run.points) {
      if (point.time === time) return point;
    }
  }
  return undefined;
}

function levelScale(values: readonly number[], innerH: number, throughZero: boolean): d3.ScaleLinear<number, number> {
  let [lo, hi] = d3.extent(values) as [number, number];
  if (throughZero) {
    lo = Math.min(lo, 0);
    hi = Math.max(hi, 0);
  }
  const pad = (hi - lo || Math.abs(hi) || 1) * 0.08;
  return d3.scaleLinear().domain([lo - pad, hi + pad]).range([innerH, 0]).nice();
}

function useBoxSize(): { ref: React.RefObject<HTMLDivElement | null>; width: number; height: number } {
  const ref = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setSize({ width: el.clientWidth, height: el.clientHeight });
    const observer = new ResizeObserver((entries) => {
      const box = entries[0]?.contentRect;
      if (!box) return;
      setSize((prev) => (
        Math.abs(prev.width - box.width) > 1 || Math.abs(prev.height - box.height) > 1
          ? { width: box.width, height: box.height }
          : prev
      ));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return { ref, ...size };
}

function signed(value: number, decimals: number): string {
  const text = Math.abs(value).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return value > 0 ? `+${text}` : value < 0 ? `-${text}` : text;
}

function plain(value: number, decimals: number): string {
  return value.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

const timeLabel = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  hour: 'numeric',
  minute: '2-digit',
});

const dayLabel = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  month: 'short',
  day: 'numeric',
});

/** One Zeroday Breadth pane: a 1-minute line over the last sessions, with open / now / move. */
export function BreadthPanel({
  label,
  points,
  color,
  overlayPoints,
  overlayColor,
  controls,
  zeroCentered = false,
  histogram = false,
  decimals,
}: BreadthPanelProps): ReactElement {
  const { ref, width, height } = useBoxSize();
  const svgRef = useRef<SVGSVGElement>(null);
  const sharedDays = useMemo(
    () => (overlayPoints ? sessionDays([points, overlayPoints]) : undefined),
    [points, overlayPoints],
  );
  const runs = useMemo(() => toSessionRuns(points, sharedDays), [points, sharedDays]);
  const overlayRuns = useMemo(
    () => (overlayPoints ? toSessionRuns(overlayPoints, sharedDays) : []),
    [overlayPoints, sharedDays],
  );
  const [hover, setHover] = useState<SlotPoint | null>(null);
  const clipId = `zb${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  // The main draw publishes its scales here so hover only repaints its own layer.
  const scalesRef = useRef<DrawnScales | null>(null);

  const current = runs[runs.length - 1];
  const open = current?.points[0];
  const last = current?.points[current.points.length - 1];
  const overlayTail = overlayRuns[overlayRuns.length - 1]?.points;
  const overlayLast = overlayTail?.[overlayTail.length - 1];
  const shown = hover ? pointAt(runs, hover.time) : last;
  const overlayShown = hover ? pointAt(overlayRuns, hover.time) : overlayLast;

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const root = d3.select(svg);
    root.selectAll('*').remove();
    scalesRef.current = null;
    if (width < 40 || height < 40 || runs.length === 0) return;

    const hasOverlay = overlayRuns.length > 0 && overlayColor != null;
    const margin = { top: 6, right: 52, bottom: 18, left: hasOverlay ? 52 : 4 };
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;
    const all = runs.flatMap((r) => r.points);
    const dayCount = sharedDays?.length ?? runs.length;

    const x = d3.scaleLinear().domain([0, dayCount * SESSION_SLOTS - 1]).range([0, innerW]);
    const y = levelScale(all.map((p) => p.value), innerH, zeroCentered || histogram);
    const yOverlay = hasOverlay
      ? levelScale(overlayRuns.flatMap((r) => r.points.map((p) => p.value)), innerH, false)
      : null;

    const g = root.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    g.append('text')
      .attr('x', innerW / 2)
      .attr('y', innerH / 2)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('fill', COLORS.watermark)
      .attr('font-size', Math.max(18, Math.min(innerH * 0.35, innerW * 0.22)))
      .attr('font-weight', 700)
      .text(label);

    const tickCount = Math.max(2, Math.floor(innerH / 40));
    const tickText = (v: d3.NumberValue): string => plain(Number(v), decimals);
    g.append('g')
      .attr('transform', `translate(${innerW},0)`)
      .call(d3.axisRight(y).ticks(tickCount).tickSize(-innerW).tickFormat(tickText))
      .call((axis) => axis.select('.domain').remove())
      .call((axis) => axis.selectAll('.tick line').attr('stroke', COLORS.gridLines).attr('stroke-dasharray', '2,2'))
      .call((axis) => axis.selectAll('.tick text').attr('fill', hasOverlay && color ? color : COLORS.text).attr('font-size', 10));

    if (yOverlay && overlayColor) {
      g.append('g')
        .call(d3.axisLeft(yOverlay).ticks(tickCount).tickSize(0).tickFormat(tickText))
        .call((axis) => axis.select('.domain').remove())
        .call((axis) => axis.selectAll('.tick text').attr('fill', overlayColor).attr('font-size', 10));
    }

    // Session boundaries and the noon mark, labelled along the bottom.
    // A day only the overlay has still needs its label, on the shared slot.
    const labelRuns = sharedDays
      ? sharedDays.flatMap((day) => {
        const found = runs.find((run) => run.day === day) ?? overlayRuns.find((run) => run.day === day);
        return found ? [found] : [];
      })
      : runs;
    for (const run of labelRuns) {
      const marks: { slot: number; text: string }[] = [
        { slot: run.start, text: dayLabel.format(new Date(run.points[0].time * 1000)) },
      ];
      const noon = slotAt(run, 12, 0);
      if (innerW / dayCount > 110 && run.points[run.points.length - 1].slot >= noon) {
        marks.push({ slot: noon, text: '12:00 PM' });
      }
      for (const mark of marks) {
        g.append('line')
          .attr('x1', x(mark.slot)).attr('x2', x(mark.slot))
          .attr('y1', 0).attr('y2', innerH)
          .attr('stroke', COLORS.gridLines);
        g.append('text')
          .attr('x', x(mark.slot) + 3)
          .attr('y', innerH + 13)
          .attr('fill', COLORS.text)
          .attr('font-size', 10)
          .text(mark.text);
      }
    }

    if (zeroCentered) {
      g.append('line')
        .attr('x1', 0).attr('x2', innerW)
        .attr('y1', y(0)).attr('y2', y(0))
        .attr('stroke', COLORS.text)
        .attr('stroke-opacity', 0.5)
        .attr('stroke-dasharray', '4,3');
    }

    // Sign colouring clips one path twice at y(0) rather than splitting segments.
    if (zeroCentered) {
      const defs = root.append('defs');
      defs.append('clipPath').attr('id', `${clipId}-up`)
        .append('rect').attr('x', 0).attr('y', 0).attr('width', innerW).attr('height', Math.max(0, y(0)));
      defs.append('clipPath').attr('id', `${clipId}-down`)
        .append('rect').attr('x', 0).attr('y', y(0)).attr('width', innerW).attr('height', Math.max(0, innerH - y(0)));
    }

    if (histogram) {
      const barW = Math.max(1, x(1) - x(0) - 0.5);
      const fill = { up: COLORS.up, down: COLORS.down, flat: COLORS.text };
      for (const run of runs) {
        run.points.forEach((p, i) => {
          const top = Math.min(y(p.value), y(0));
          g.append('rect')
            .attr('x', x(p.slot) - barW / 2)
            .attr('y', top)
            .attr('width', barW)
            .attr('height', Math.max(0.5, Math.abs(y(p.value) - y(0))))
            .attr('fill', fill[histogramTone(p.value, run.points[i - 1]?.value)])
            .attr('fill-opacity', 0.85);
        });
      }
    }

    if (yOverlay && overlayColor) {
      const overlayLine = d3.line<SlotPoint>().x((p) => x(p.slot)).y((p) => yOverlay(p.value));
      for (const run of overlayRuns) {
        g.append('path')
          .datum(run.points)
          .attr('fill', 'none')
          .attr('stroke', overlayColor)
          .attr('stroke-width', 1.5)
          .attr('d', overlayLine);
      }
      const overlayAll = overlayRuns.flatMap((run) => run.points);
      const overlayTailPoint = overlayAll[overlayAll.length - 1];
      if (overlayTailPoint) {
        const overlayTag = g.append('g').attr('transform', `translate(0,${yOverlay(overlayTailPoint.value)})`);
        overlayTag.append('rect')
          .attr('x', 2 - margin.left).attr('y', -8).attr('width', margin.left - 3).attr('height', 16).attr('rx', 2)
          .attr('fill', overlayColor);
        overlayTag.append('text')
          .attr('x', -4).attr('y', 4)
          .attr('text-anchor', 'end')
          .attr('fill', '#fff')
          .attr('font-size', 10)
          .attr('font-weight', 600)
          .text(plain(overlayTailPoint.value, decimals));
      }
    }

    const line = d3.line<SlotPoint>().x((p) => x(p.slot)).y((p) => y(p.value));
    const strokes = zeroCentered
      ? [{ stroke: COLORS.up, clip: `${clipId}-up` }, { stroke: COLORS.down, clip: `${clipId}-down` }]
      : [{ stroke: color ?? COLORS.up, clip: null }];
    for (const run of histogram ? [] : runs) {
      for (const { stroke, clip } of strokes) {
        const path = g.append('path')
          .datum(run.points)
          .attr('fill', 'none')
          .attr('stroke', stroke)
          .attr('stroke-width', 1.5)
          .attr('d', line);
        if (clip) path.attr('clip-path', `url(#${clip})`);
      }
    }

    const tail = all[all.length - 1];
    const tagColor = zeroCentered || histogram ? (tail.value >= 0 ? COLORS.up : COLORS.down) : (color ?? COLORS.up);
    const tag = g.append('g').attr('transform', `translate(${innerW},${y(tail.value)})`);
    tag.append('rect').attr('x', 1).attr('y', -8).attr('width', margin.right - 2).attr('height', 16).attr('rx', 2).attr('fill', tagColor);
    tag.append('text')
      .attr('x', 4).attr('y', 4)
      .attr('fill', '#fff')
      .attr('font-size', 10)
      .attr('font-weight', 600)
      .text(plain(tail.value, decimals));

    g.append('g').attr('class', 'hover-layer').attr('pointer-events', 'none');
    scalesRef.current = { x, y, yOverlay, innerH, color: tagColor, overlayColor: hasOverlay ? overlayColor : null };

    const hoverPoints = hasOverlay
      ? [...all, ...overlayRuns.flatMap((run) => run.points)].sort((a, b) => a.slot - b.slot)
      : all;
    const bisect = d3.bisector<SlotPoint, number>((p) => p.slot).center;
    g.append('rect')
      .attr('width', innerW)
      .attr('height', innerH)
      .attr('fill', 'transparent')
      .on('mousemove', (event: MouseEvent) => {
        const [mx] = d3.pointer(event);
        const index = bisect(hoverPoints, x.invert(mx));
        setHover(hoverPoints[index] ?? null);
      })
      .on('mouseleave', () => setHover(null));
  }, [runs, overlayRuns, sharedDays, width, height, label, color, overlayColor, zeroCentered, histogram, decimals, clipId]);

  useEffect(() => {
    const svg = svgRef.current;
    const scales = scalesRef.current;
    if (!svg) return;
    const layer = d3.select(svg).select<SVGGElement>('.hover-layer');
    layer.selectAll('*').remove();
    if (!hover || !scales) return;
    const { x, y, yOverlay, innerH, color: dot, overlayColor: overlayDot } = scales;
    layer.append('line')
      .attr('x1', x(hover.slot)).attr('x2', x(hover.slot))
      .attr('y1', 0).attr('y2', innerH)
      .attr('stroke', COLORS.crosshair)
      .attr('stroke-dasharray', '3,3');
    const primaryHit = pointAt(runs, hover.time);
    if (primaryHit) {
      layer.append('circle').attr('cx', x(hover.slot)).attr('cy', y(primaryHit.value)).attr('r', 2.5).attr('fill', dot);
    }
    const overlayHit = yOverlay ? pointAt(overlayRuns, hover.time) : undefined;
    if (overlayHit && yOverlay && overlayDot) {
      layer.append('circle')
        .attr('cx', x(hover.slot)).attr('cy', yOverlay(overlayHit.value)).attr('r', 2.5).attr('fill', overlayDot);
    }
  }, [hover, runs, overlayRuns, width, height]);

  const move = open && last ? last.value - open.value : null;

  return (
    <div className="flex flex-col min-h-0 min-w-0 bg-dark-bg border border-dark-border rounded">
      <div className="flex items-center gap-2 px-2 py-1 text-[11px] font-numeric border-b border-dark-border min-w-0">
        {controls ?? <span className="font-bold text-white tracking-wide">{label}</span>}
        {shown && (
          <span className="truncate">
            {hover && <span className="text-gray-500 mr-1">{timeLabel.format(new Date(hover.time * 1000))}</span>}
            <span className={color ? undefined : 'text-gray-300'} style={color ? { color } : undefined}>
              {plain(shown.value, decimals)}
            </span>
          </span>
        )}
        {overlayShown && overlayColor && (
          <span style={{ color: overlayColor }}>{plain(overlayShown.value, decimals)}</span>
        )}
        {!hover && !histogram && !overlayPoints && open && (
          <span className="text-gray-500">
            Open <span className="text-gray-300">{plain(open.value, decimals)}</span>
          </span>
        )}
        {!hover && !histogram && !overlayPoints && move != null && (
          <span className={move >= 0 ? 'text-bull-blue' : 'text-bear-red'}>{signed(move, decimals)}</span>
        )}
      </div>
      <div ref={ref} className="relative flex-1 min-h-0">
        {points.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-500">No data yet</div>
        ) : (
          <svg ref={svgRef} className="absolute inset-0 w-full h-full" />
        )}
      </div>
    </div>
  );
}
